// The TV path into the launch dashboard. This renders OUTSIDE the router and
// the signed-in shell (App.tsx boots it directly), because the kiosk machine
// has no Google sign-in: its credential is a remote-view token redeemed from a
// one-time code an operator minted on /launch.
//
// Three states: a live stored session → straight to the dashboard; a ?code= in
// the URL (or one typed into the form) → redeem it; otherwise → the code form.
//
// The session is RETIRED here, not on the dashboard, because this component is
// what can act on it: dropping back to the code form is the only screen a TV
// with a dead token can do anything from. Two things retire it — the expiry
// clock we already know (`launchSessionExpiryDelay`) and a server rejection the
// dashboard reports back through `onSessionEnded` — and either way the polling
// stops with it.
import { type FormEvent, useCallback, useEffect, useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { Button, Input } from "@recruiter/shared";
import type { LaunchRedeemResponse } from "@recruiter/shared/types";

import { redeemLaunchCode } from "../lib/api";
import {
  clearLaunchSession,
  launchSessionExpiryDelay,
  type LaunchSession,
  normalizeLaunchCode,
  readLaunchSession,
  storeLaunchSession,
  themeFromSearch,
} from "../lib/launch";
import { applyTheme } from "../lib/theme-client";
import { errorMessage } from "../components/states";
import launchArt from "../assets/launch-day.png";
import { LaunchPage } from "./launch";
import "./launch.css";

function codeFromUrl(): string {
  const code = new URLSearchParams(window.location.search).get("code") ?? "";
  return normalizeLaunchCode(code);
}

/** Drop the one-time code from the address bar once consumed, so a reload or a
 *  cast/share of the TV screen never re-shows a (now dead) credential. */
function stripCodeFromUrl(): void {
  window.history.replaceState({}, "", "/launch");
}

export function LaunchKiosk() {
  const [session, setSession] = useState<LaunchSession | null>(() => readLaunchSession());
  const [expired, setExpired] = useState(false);
  const [typed, setTyped] = useState("");

  const redeem = useMutation<LaunchRedeemResponse, unknown, string>({
    mutationFn: redeemLaunchCode,
    onSuccess: (res) => {
      const next: LaunchSession = { token: res.token, expiresAt: res.expiresAt };
      storeLaunchSession(next);
      stripCodeFromUrl();
      setExpired(false);
      setSession(next);
    },
  });

  /** Retire the remote-view credential: forget it, unmount the dashboard (which
   *  stops its refetch timer with it), and put the code form back up so whoever
   *  walks past the TV has something to act on. */
  const endSession = useCallback(() => {
    clearLaunchSession();
    setSession(null);
    setExpired(true);
  }, []);

  // A code arriving in the URL redeems itself, once. A `?theme=` riding along
  // is applied (and persisted) FIRST, because the redeem success strips the
  // whole query string from the address bar.
  const urlCode = codeFromUrl();
  useEffect(() => {
    const requested = themeFromSearch(window.location.search);
    if (requested) applyTheme(requested);
    if (!session && urlCode && redeem.isIdle) redeem.mutate(urlCode);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- fire once per mount
  }, []);

  // Retire the token on its OWN clock. Waiting for the dashboard to be refused
  // would mean polling a dead credential until the moment somebody looks at the
  // screen; here the TV goes quiet at expiry with zero failed requests.
  useEffect(() => {
    if (!session) return;
    const id = window.setTimeout(endSession, launchSessionExpiryDelay(session));
    return () => window.clearTimeout(id);
  }, [session, endSession]);

  if (session) return <LaunchPage session={session} onSessionEnded={endSession} />;

  const onSubmit = (event: FormEvent) => {
    event.preventDefault();
    const code = normalizeLaunchCode(typed);
    if (code) redeem.mutate(code);
  };

  return (
    <div className="lp lp--kiosk lp-gate">
      <form className="lp-gate__panel" onSubmit={onSubmit}>
        <img className="lp-gate__art" src={launchArt} alt="" role="presentation" />
        <h1 className="lp-gate__title">Mira launch day</h1>
        <p className="lp-gate__hint">
          Ask an operator to press "Open in remote" on the /launch page, then type the code here.
        </p>
        <Input
          value={typed}
          onChange={(e) => setTyped(e.target.value)}
          placeholder="ABC 123"
          aria-label="One-time code"
          autoFocus
          className="lp-gate__input"
        />
        {redeem.isError ? (
          <p className="lp-gate__error">{errorMessage(redeem.error)}</p>
        ) : expired ? (
          <p className="lp-gate__error">
            This remote link has expired, so the board stopped refreshing. Type a fresh code to
            bring it back.
          </p>
        ) : null}
        <Button type="submit" disabled={redeem.isPending || !normalizeLaunchCode(typed)}>
          {redeem.isPending ? "Checking…" : "Open dashboard"}
        </Button>
      </form>
    </div>
  );
}
